/**
 * B797-wallet (Pathik 2026-10-13, on the confirmation's ticket rows: "we
 * dont need that advanced toggle which shows the barcode. we need to add
 * 'Add to Apple Wallet' and 'Add to Google Wallet' the passkeys should be
 * branded to the tenant") — a ticket as a WALLET PASS, in the tenant's name,
 * logo and colors, carrying the SAME ttk1 token the gate scans (§8.1: one
 * mint path, one barcode — the pass, the email and the confirmation page
 * are byte-equal at the scanner).
 *
 * This module is the PURE half: the facts a pass draws, Apple's pass.json,
 * Google's event-ticket class + object, the manifest and the stored-zip
 * container `.pkpass` / `.pkpasses` are. Signing is a port (Apple's PKCS#7
 * detached signature under the Pass Type ID certificate; Google's RS256
 * JWT under the issuer's service account) — the handler holds the ports,
 * the stage's secret holds the material, and a stage without either simply
 * offers no button (an unlit door is honest; a door that opens nothing is
 * not — 74:2's own instruction).
 *
 * Every word on a pass is a read's (the email's own view: product, slot,
 * the tenant's trading name, the order's channel theme); a missing fact
 * leaves its field absent, never a placeholder. Nothing here is I/O.
 */
import type { ChannelTheme } from "./theming.js";
import { THEME_DEFAULTS } from "./theming.js";
import type { TicketEmailCard, TicketEmailView } from "./ticket-email.js";
import { ticketEmailView, type TicketEmailFactPorts } from "./ticket-email-facts.js";
import type { Entitlement } from "./entitlement.js";
import type { EntitlementStore } from "./redemption.js";
import type { OrderStore } from "./order-store.js";

/* ------------------------------------------------------------- the facts */

export interface WalletPassFacts {
  /** The tenant's trading name — the pass's organization and logo text. */
  readonly tenantName: string;
  /** https only (the theme's own wall); absent = the name stands alone. */
  readonly logoUrl?: string;
  /** The brand's ink pair, #rrggbb — the theme's accent on its contrast. */
  readonly backgroundHex: string;
  readonly foregroundHex: string;
  readonly labelHex: string;
  /** "Harbor Ferry Crossing" — the show / product. */
  readonly show: string;
  /** "Sun, Oct 4 · 10:30" — the slot, when the ticket is bound to one. */
  readonly when?: string;
  /** "Adult · 2 of 8" — the email card's own label. */
  readonly ticketLabel: string;
  /** The human confirmation code, when the order carries one. */
  readonly orderCode?: string;
  readonly orderId: string;
  readonly entitlementId: string;
  /** The ttk1 token — the barcode's message, verbatim. */
  readonly token: string;
  /** Validity end (epoch ms) — the pass's expiration. */
  readonly validUntilMs?: number;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function pick(theme: ChannelTheme | undefined, role: keyof typeof THEME_DEFAULTS): string {
  const v = theme?.colors?.[role];
  return v !== undefined && HEX.test(v) ? v.toLowerCase() : THEME_DEFAULTS[role];
}

/** "#3d7fe8" → "rgb(61, 127, 232)" — Apple's pass.json color form. */
export function rgbWords(hex: string): string {
  const h = hex.replace("#", "");
  const n = (i: number): number => Number.parseInt(h.slice(i, i + 2), 16);
  return `rgb(${String(n(0))}, ${String(n(2))}, ${String(n(4))})`;
}

/**
 * One ticket's facts off the order's email view (ticketEmailView — the one
 * derivation the email, the ticket page and the door already share) and
 * its minted token. The brand is the ORDER's channel theme: accent as the
 * pass ground, its contrast as the ink, the muted ink for labels.
 */
export function walletPassFactsFor(input: {
  readonly view: TicketEmailView;
  readonly card: TicketEmailCard;
  readonly orderId: string;
  readonly entitlementId: string;
  readonly token: string;
  readonly validUntilMs?: number;
}): WalletPassFacts {
  const { view, card } = input;
  const theme = view.brand.theme;
  const name = view.brand.name?.trim();
  return Object.freeze({
    tenantName: name !== undefined && name !== "" ? name : "Your tickets",
    ...(view.brand.logoUrl !== undefined && view.brand.logoUrl.startsWith("https://") ? { logoUrl: view.brand.logoUrl } : {}),
    backgroundHex: pick(theme, "accent"),
    foregroundHex: pick(theme, "accentContrast"),
    labelHex: pick(theme, "accentContrast"),
    show: view.productName ?? card.label,
    ...(card.when !== undefined ? { when: card.when } : {}),
    ticketLabel: card.label,
    ...(view.confirmationCode !== undefined ? { orderCode: view.confirmationCode } : {}),
    orderId: input.orderId,
    entitlementId: input.entitlementId,
    token: input.token,
    ...(input.validUntilMs !== undefined ? { validUntilMs: input.validUntilMs } : {}),
  });
}

/* ------------------------------------------------------------ Apple pass */

export interface ApplePassIdentity {
  /** "pass.ai.gotransact.ticket" — the Pass Type ID the certificate was issued for. */
  readonly passTypeIdentifier: string;
  /** The Apple Developer team the certificate belongs to. */
  readonly teamIdentifier: string;
}

/** Apple's pass.json for an EVENT TICKET — one ticket, the tenant's brand, the ttk1 QR. */
export function applePassJson(facts: WalletPassFacts, id: ApplePassIdentity): Record<string, unknown> {
  const secondary = [
    ...(facts.when !== undefined ? [{ key: "when", label: "WHEN", value: facts.when }] : []),
    { key: "ticket", label: "TICKET", value: facts.ticketLabel },
  ];
  const back = [
    ...(facts.orderCode !== undefined ? [{ key: "code", label: "Confirmation code", value: facts.orderCode }] : []),
    { key: "order", label: "Order", value: facts.orderId },
    { key: "gate", label: "At the gate", value: "Show this pass — the code is scanned once, at the door." },
  ];
  return {
    formatVersion: 1,
    passTypeIdentifier: id.passTypeIdentifier,
    teamIdentifier: id.teamIdentifier,
    serialNumber: facts.entitlementId,
    organizationName: facts.tenantName,
    description: `${facts.tenantName} — ${facts.show}`,
    logoText: facts.tenantName,
    backgroundColor: rgbWords(facts.backgroundHex),
    foregroundColor: rgbWords(facts.foregroundHex),
    labelColor: rgbWords(facts.labelHex),
    ...(facts.validUntilMs !== undefined ? { expirationDate: new Date(facts.validUntilMs).toISOString() } : {}),
    barcodes: [{ format: "PKBarcodeFormatQR", message: facts.token, messageEncoding: "iso-8859-1", altText: facts.orderCode ?? undefined }],
    eventTicket: {
      primaryFields: [{ key: "show", label: "", value: facts.show }],
      secondaryFields: secondary,
      backFields: back,
    },
  };
}

/* ------------------------------------------------------------ Google pass */

export interface GoogleWalletIdentity {
  /** The issuer id Google assigned to the tenant platform account. */
  readonly issuerId: string;
}

/** Google's class id: one class per (issuer, tenant-ish show) — kept per order so a class never grows stale fields. */
export function googleClassId(id: GoogleWalletIdentity, orderId: string): string {
  return `${id.issuerId}.${safeId(orderId)}`;
}

export function googleObjectId(id: GoogleWalletIdentity, entitlementId: string): string {
  return `${id.issuerId}.${safeId(entitlementId)}`;
}

/** Google allows [A-Za-z0-9._-] after the issuer dot; anything else becomes "-". */
function safeId(s: string): string {
  return s.replace(/[^A-Za-z0-9._-]/g, "-");
}

/** The event-ticket CLASS (the show, the venue words, the brand) and OBJECT (this ticket, its QR) a Save link carries inline. */
export function googleEventTicket(facts: WalletPassFacts, id: GoogleWalletIdentity): {
  readonly eventTicketClass: Record<string, unknown>;
  readonly eventTicketObject: Record<string, unknown>;
} {
  const classId = googleClassId(id, facts.orderId);
  const eventTicketClass: Record<string, unknown> = {
    id: classId,
    issuerName: facts.tenantName,
    reviewStatus: "UNDER_REVIEW",
    eventName: { defaultValue: { language: "en", value: facts.show } },
    hexBackgroundColor: facts.backgroundHex,
    ...(facts.logoUrl !== undefined ? { logo: { sourceUri: { uri: facts.logoUrl } } } : {}),
  };
  const eventTicketObject: Record<string, unknown> = {
    id: googleObjectId(id, facts.entitlementId),
    classId,
    state: "ACTIVE",
    barcode: { type: "QR_CODE", value: facts.token, ...(facts.orderCode !== undefined ? { alternateText: facts.orderCode } : {}) },
    ticketType: { defaultValue: { language: "en", value: facts.ticketLabel } },
    ...(facts.when !== undefined ? { textModulesData: [{ id: "when", header: "When", body: facts.when }] } : {}),
    ...(facts.orderCode !== undefined ? { ticketNumber: facts.orderCode } : {}),
    ...(facts.validUntilMs !== undefined
      ? { validTimeInterval: { end: { date: new Date(facts.validUntilMs).toISOString() } } }
      : {}),
  };
  return { eventTicketClass, eventTicketObject };
}

/** The Save-to-Google-Wallet JWT claims for a set of tickets (classes + objects inline; Google creates what it has not seen). */
export function googleSaveClaims(input: {
  readonly serviceAccountEmail: string;
  readonly origins: readonly string[];
  readonly tickets: readonly { readonly eventTicketClass: Record<string, unknown>; readonly eventTicketObject: Record<string, unknown> }[];
  readonly nowMs: number;
}): Record<string, unknown> {
  const classes = new Map<string, Record<string, unknown>>();
  for (const t of input.tickets) classes.set(String(t.eventTicketClass["id"]), t.eventTicketClass);
  return {
    iss: input.serviceAccountEmail,
    aud: "google",
    typ: "savetowallet",
    iat: Math.floor(input.nowMs / 1000),
    origins: [...input.origins],
    payload: {
      eventTicketClasses: [...classes.values()],
      eventTicketObjects: input.tickets.map((t) => t.eventTicketObject),
    },
  };
}

export function googleSaveUrl(jwt: string): string {
  return `https://pay.google.com/gp/v/save/${jwt}`;
}

/* ------------------------------------------------- the .pkpass container */

/** A file inside the bundle: its name and bytes. */
export interface BundleFile {
  readonly name: string;
  readonly bytes: Uint8Array;
}

/** Apple's manifest.json: every file's SHA-1, hex, keyed by name. The hash is a port (no crypto in the domain). */
export function manifestFor(files: readonly BundleFile[], sha1Hex: (bytes: Uint8Array) => string): string {
  const out: Record<string, string> = {};
  for (const f of files) out[f.name] = sha1Hex(f.bytes);
  return JSON.stringify(out);
}

/* --- a STORED zip (no compression — the files are tiny and Apple reads any method) --- */

const CRC_TABLE: Uint32Array = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = (CRC_TABLE[(c ^ (bytes[i] as number)) & 0xff] as number) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function le16(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff];
}
function le32(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];
}

/** Every file stored (method 0), one central directory, no zip64 — the pkpass shape. Pure bytes in, pure bytes out. */
export function storedZip(files: readonly BundleFile[]): Uint8Array {
  const enc = new TextEncoder();
  const locals: number[] = [];
  const central: number[] = [];
  let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.bytes);
    const head = [0x50, 0x4b, 0x03, 0x04, ...le16(20), ...le16(0x0800), ...le16(0), ...le16(0), ...le16(0x21), ...le32(crc), ...le32(f.bytes.length), ...le32(f.bytes.length), ...le16(name.length), ...le16(0)];
    locals.push(...head, ...name, ...f.bytes);
    central.push(0x50, 0x4b, 0x01, 0x02, ...le16(20), ...le16(20), ...le16(0x0800), ...le16(0), ...le16(0), ...le16(0x21), ...le32(crc), ...le32(f.bytes.length), ...le32(f.bytes.length), ...le16(name.length), ...le16(0), ...le16(0), ...le16(0), ...le16(0), ...le32(0), ...le32(offset), ...name);
    offset += head.length + name.length + f.bytes.length;
  }
  const end = [0x50, 0x4b, 0x05, 0x06, ...le16(0), ...le16(0), ...le16(files.length), ...le16(files.length), ...le32(central.length), ...le32(offset), ...le16(0)];
  return Uint8Array.from([...locals, ...central, ...end]);
}

/** The signed bundle: pass.json + images + manifest.json + signature, stored. */
export function pkpassBundle(input: {
  readonly passJson: Record<string, unknown>;
  readonly images: readonly BundleFile[];
  readonly sha1Hex: (bytes: Uint8Array) => string;
  /** The PKCS#7 detached signature over the manifest bytes — the port's answer. */
  readonly signature: Uint8Array;
  readonly manifest: string;
}): Uint8Array {
  const enc = new TextEncoder();
  return storedZip([
    { name: "pass.json", bytes: enc.encode(JSON.stringify(input.passJson)) },
    ...input.images,
    { name: "manifest.json", bytes: enc.encode(input.manifest) },
    { name: "signature", bytes: input.signature },
  ]);
}

/** Several passes in one tap: Apple's `.pkpasses` is a zip of `.pkpass` files. */
export function pkpassesBundle(passes: readonly { readonly serial: string; readonly bytes: Uint8Array }[]): Uint8Array {
  return storedZip(passes.map((p) => ({ name: `${safeId(p.serial)}.pkpass`, bytes: p.bytes })));
}

/** The files Apple hashes — pass.json first, the images, never the manifest or the signature. */
export function pkpassManifestFiles(passJson: Record<string, unknown>, images: readonly BundleFile[]): readonly BundleFile[] {
  const enc = new TextEncoder();
  return [{ name: "pass.json", bytes: enc.encode(JSON.stringify(passJson)) }, ...images];
}

/* ----------------------------------------------- an order's wallet passes */

export interface WalletOrderDeps extends TicketEmailFactPorts {
  readonly entitlements: Pick<EntitlementStore, "listByOrder">;
  readonly orders: Pick<OrderStore, "get">;
}

/**
 * The order's LIVE tickets, each with its email card — the SAME derivation
 * the email, the ticket page and the door use (ticketEmailView over the
 * order's tickets in id order, so "2 of 3" numbers the way the email does).
 * A voided ticket draws no pass. undefined = no such order (the uniform
 * miss; the fence is the handler's).
 */
export async function walletOrderPasses(
  deps: WalletOrderDeps,
  tenantId: string,
  orderId: string,
): Promise<{ readonly view: TicketEmailView; readonly tickets: readonly { readonly entitlement: Entitlement; readonly card: TicketEmailCard }[] } | undefined> {
  const order = (await deps.orders.get(tenantId, orderId))?.record;
  if (order === undefined) return undefined;
  const all = [...(await deps.entitlements.listByOrder(tenantId, orderId))].sort((a, b) =>
    a.entitlementId < b.entitlementId ? -1 : a.entitlementId > b.entitlementId ? 1 : 0,
  );
  const view = await ticketEmailView(deps, {
    tenantId,
    order,
    tickets: all.map((entitlement, i) => ({ entitlement, qrContentId: `t-${String(i)}` })),
  });
  const tickets = all.flatMap((entitlement, i) => {
    const card = view.cards[i];
    return card === undefined || entitlement.state === "VOIDED" ? [] : [{ entitlement, card }];
  });
  return { view, tickets };
}
